import axios, { AxiosInstance } from "axios";
import { LocalStorageCache } from "@auth0/auth0-react";

/** Resolves the current Auth0 access token, or nothing when signed out. */
export type AccessTokenProvider = () => Promise<string | undefined>;

let accessTokenProvider: AccessTokenProvider | null = null;

/**
 * Register the Auth0 SDK's token getter (see AuthTokenBridge). The SDK refreshes
 * expired tokens; the localStorage fallback below only sees whatever was cached.
 */
export const registerAccessTokenProvider = (provider: AccessTokenProvider | null): void => {
    accessTokenProvider = provider;
};

/**
 * Find a cached Auth0 access token in local storage. Fallback for requests made
 * before AuthTokenBridge has registered the SDK getter.
 */
const getCachedAccessToken = (): string | null => {
    try {
        const allKeys = new LocalStorageCache().allKeys() as string[];
        for (const key of allKeys) {
            const raw = localStorage.getItem(key);
            const parsed = raw ? JSON.parse(raw) : undefined;
            const token = parsed?.body?.access_token as string | undefined;
            if (token) return token;
        }
    } catch {
        // Storage unavailable or unparsable: treat as signed out.
    }
    return null;
};

const resolveAccessToken = async (): Promise<string | null> => {
    if (accessTokenProvider) {
        try {
            const token = await accessTokenProvider();
            if (token) return token;
        } catch {
            // Not signed in (login_required etc.): fall through to the cache.
        }
    }
    return getCachedAccessToken();
};

// Use this instance of axios for authenticated requests
const axiosInstance: AxiosInstance = axios.create();

// Attach the freshest Auth0 access token to every request
axiosInstance.interceptors.request.use(async (config) => {
    const token = await resolveAccessToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Add response interceptor for error logging
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Import logger dynamically to avoid circular dependencies
        const { logger } = await import("@/services/LoggerService");

        if (error.response) {
            // Server responded with error status
            logger.error("API request failed", error, {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response.status,
                statusText: error.response.statusText,
                responseData: error.response.data,
                context: "axiosInterceptor",
            });
        } else if (error.request) {
            // Request was made but no response received
            logger.error("API request failed - No response", error, {
                url: error.config?.url,
                method: error.config?.method,
                context: "axiosInterceptor",
                errorType: "network",
            });
        } else {
            // Something else happened
            logger.error("API request error", error, {
                url: error.config?.url,
                method: error.config?.method,
                context: "axiosInterceptor",
            });
        }

        return Promise.reject(error);
    },
);

export default axiosInstance;
